import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement, useEffect, useRef } from 'react';
import { createPopper } from '@popperjs/core';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle';
import { getLocalizedProperty, uuid } from '@loyalty/shared-common';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { AccessibleDate } from '../transaction-history-component-v2/AccessibleDate';
import { CurrencyCounterSkeleton } from './CurrencyCounterSkeleton';
import { GenericComponent } from '../../components-tree';
import { ReactBootstrap } from '../../ReactBootstrap';
import { TranslationProvider } from '../TranslationProvider/TranslationProvider';
import { WidgetProps } from '../render-component';

export const CLASSNAME = 'qlfeditor-currency-counter';

export interface CurrencyCounterAttributes {
  showTotal: boolean;
  showCurrencyPicture: boolean;
  showCurrencyName: boolean;
  showExpiration: boolean;
  currencyId: string;
}

type CurrencyCounterComponent = GenericComponent<CurrencyCounterAttributes>;

interface CurrencyCounterFrontendProps {
  id: string;
  attributes: CurrencyCounterComponent['attributes'];
  currencyImage?: string;
  currencyName?: string;
  formattedAmount: string;
  formattedTotalAmount: string;
  formattedExpirationAmount?: string;
  expirationDate?: string;
}

export type CurrencyCounterBackendProps = WidgetProps<CurrencyCounterComponent>;

export function serializeCurrencyCounterProps({
  state,
  options,
  context,
}: CurrencyCounterBackendProps): CurrencyCounterFrontendProps {
  const { profile, currencies } = state;
  const currencyId = options.attributes.currencyId;
  const locale = context.i18n.locale;
  const currencyBalance = profile.currencies.find(cb => cb.currencyId === currencyId);
  const currency = currencies.find(c => c.id === currencyId);
  const currencyImage = getLocalizedProperty(currency, 'image', locale);
  const currencyName = getLocalizedProperty(currency, 'name', locale);
  const formattedAmount = context.i18n.formatNumber(currencyBalance?.value ?? 0);
  const formattedTotalAmount = context.i18n.formatNumber(currencyBalance?.totalAccumulated ?? 0);
  const expiration = currencyBalance?.nextExpiration;
  const formattedExpirationAmount = expiration?.amount
    ? context.i18n.formatNumber(expiration?.amount)
    : undefined;
  const expirationDate = expiration?.date;

  return {
    attributes: options.attributes,
    currencyName,
    currencyImage,
    formattedAmount,
    formattedTotalAmount,
    id: options.id || options.attributes.id || uuid(),
    formattedExpirationAmount,
    expirationDate,
  };
}

function Component(minProps: CurrencyCounterFrontendProps): ReactElement {
  const { t } = useTranslation('participant');
  const {
    currencyImage,
    currencyName,
    attributes,
    formattedAmount,
    formattedTotalAmount,
    id,
    formattedExpirationAmount,
    expirationDate,
  } = minProps;
  const {
    showCurrencyPicture,
    showCurrencyName,
    showTotal,
    showExpiration,
    class: className,
  } = attributes;

  const currencyTooltip = t('editor.currency-counter.tooltip', {
    currency: currencyName,
  });
  const expirationTooltip = t('editor.currency-counter.expireOn');

  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;

    if (!trigger || !tooltip) return;

    const popperInstance = createPopper(trigger, tooltip, {
      placement: 'top',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ],
    });

    const showEvents = ['mouseenter', 'focus'];
    const hideEvents = ['mouseleave', 'blur'];

    const handleShow = () => {
      tooltip.classList.remove('hidden');
      popperInstance.update();
    };

    const handleHide = () => {
      tooltip.classList.add('hidden');
    };

    showEvents.forEach(event => {
      trigger.addEventListener(event, handleShow);
    });

    hideEvents.forEach(event => {
      trigger.addEventListener(event, handleHide);
    });

    return () => {
      showEvents.forEach(event => {
        trigger.removeEventListener(event, handleShow);
      });
      hideEvents.forEach(event => {
        trigger.removeEventListener(event, handleHide);
      });
      popperInstance.destroy();
    };
  }, []);

  const currencyTooltipId = `currency_tooltip_${id}`;
  return (
    <section
      className={classNames(CLASSNAME, className)}
      aria-label={t('aria.currency-counter.label')}
      aria-description={t('aria.currency-counter.description')}
    >
      {showCurrencyPicture && currencyImage && (
        <div className={`${CLASSNAME}__image`}>
          <img src={currencyImage} alt="" />
        </div>
      )}
      <div className={`${CLASSNAME}__points`}>{formattedAmount}</div>
      {showCurrencyName && currencyName && (
        <div className={`${CLASSNAME}__name`}>{currencyName}</div>
      )}
      {showTotal && (
        <div className={`${CLASSNAME}__total`}>
          <span className={`${CLASSNAME}__total-name`}>Total:</span>
          {showCurrencyPicture && currencyImage && (
            <div className={`${CLASSNAME}__total-image`}>
              <img src={currencyImage} alt="" />
            </div>
          )}
          <span className={`${CLASSNAME}__total-points`}>{formattedTotalAmount}</span>
          <span className={`${CLASSNAME}__total-points`}>{showCurrencyName && currencyName}</span>

          <div
            ref={triggerRef}
            className="currency-tooltip-trigger"
            style={{ marginLeft: '5px' }}
            tabIndex={0}
            aria-describedby={currencyTooltipId}
          >
            <FontAwesomeIcon
              aria-hidden="true"
              icon={faQuestionCircle}
              style={{
                fontSize: '1em',
              }}
            />
            {/*<i className="far fa-question-circle fa-2x" />*/}
          </div>
          <div
            ref={tooltipRef}
            id={currencyTooltipId}
            className="tooltip-body hidden"
            role="tooltip"
          >
            {currencyTooltip}
            <div className="tooltip-arrow" data-popper-arrow></div>
          </div>
        </div>
      )}
      {showExpiration && formattedExpirationAmount && expirationDate && (
        <div className={`${CLASSNAME}__expiration ${attributes.class}`}>
          <span className={`${CLASSNAME}__expiration_amount`}>{formattedExpirationAmount}</span>
          <span className={`${CLASSNAME}__expiration_amount`}>{expirationTooltip}</span>
          <span className={`${CLASSNAME}__expiration_amount`}>
            {<AccessibleDate date={new Date(expirationDate)} />}
          </span>
        </div>
      )}
    </section>
  );
}

export function CurrencyCounterComponent(props: CurrencyCounterFrontendProps) {
  return (
    <TranslationProvider loader={<CurrencyCounterSkeleton />}>
      <Component {...props} />
    </TranslationProvider>
  );
}

export function CurrencyCounterRenderer(
  props: CurrencyCounterBackendProps,
  document?: Document,
): string {
  const reactProps = serializeCurrencyCounterProps(props);
  return renderToString(
    <ReactBootstrap
      component="CurrencyCounterComponent"
      props={reactProps}
      document={document}
      componentType="currency-counter"
    />,
  );
}
